import { forwardRef, useState, type InputHTMLAttributes } from 'react';

import { cx } from '../lib/cx';

import { IconButton } from './IconButton';

export interface PasswordInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  invalid?: boolean;
}

/** An open eye — "show password". Local to this file; not a nav/shell icon. */
function EyeIcon(): React.JSX.Element {
  return (
    <svg width={15} height={15} viewBox="0 0 16 16" aria-hidden="true" focusable={false}>
      <g fill="none" stroke="currentColor" strokeWidth={1.5}>
        <path d="M1 8 C1 8 4 3 8 3 C12 3 15 8 15 8 C15 8 12 13 8 13 C4 13 1 8 1 8 Z" />
        <circle cx="8" cy="8" r="2" />
      </g>
    </svg>
  );
}

/** An eye with a strike-through — "hide password". */
function EyeOffIcon(): React.JSX.Element {
  return (
    <svg width={15} height={15} viewBox="0 0 16 16" aria-hidden="true" focusable={false}>
      <g fill="none" stroke="currentColor" strokeWidth={1.5}>
        <path d="M1 8 C1 8 4 3 8 3 C12 3 15 8 15 8 C15 8 12 13 8 13 C4 13 1 8 1 8 Z" />
        <circle cx="8" cy="8" r="2" />
        <line x1="2" y1="14" x2="14" y2="2" />
      </g>
    </svg>
  );
}

/**
 * A password field with a show/hide toggle, so what was typed can be checked
 * before submitting rather than trusted blind. The toggle is a real, labelled
 * button — never a bare icon — and `type="button"` so it can never submit an
 * enclosing form.
 *
 * `className` is the input's own visual skin (`ds-input` for the shared
 * design system, a screen's bespoke class elsewhere) — unlike `Input`, it is
 * not defaulted here, since the login screen's `.login-input` and the
 * generic `.ds-input` genuinely look different and both need this toggle.
 */
export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  function PasswordInput({ invalid, className, ...rest }, ref) {
    const [visible, setVisible] = useState(false);

    return (
      <div className="ds-password">
        <input
          ref={ref}
          type={visible ? 'text' : 'password'}
          className={cx('ds-password__input', className)}
          aria-invalid={invalid === true ? true : undefined}
          {...rest}
        />
        <IconButton
          label={visible ? 'Hide password' : 'Show password'}
          tone="bare"
          className="ds-password__toggle"
          onClick={() => {
            setVisible((current) => !current);
          }}
        >
          {visible ? <EyeOffIcon /> : <EyeIcon />}
        </IconButton>
      </div>
    );
  },
);
